import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import {
    AuthError,
    createClient,
    Provider,
    Session,
} from '@supabase/supabase-js';

import { useToast } from 'hooks/useToast';

import { Database } from 'libs/supabaseTypes';

const { REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_ANON_KEY } = process.env;

export const supabase = createClient<Database>(
    REACT_APP_SUPABASE_URL ?? '',
    REACT_APP_SUPABASE_ANON_KEY ?? ''
);

export type ProfileData = Database['public']['Tables']['profiles']['Row'];

interface AuthContextState {
    signUp:
        | (({
              email,
              password,
              captchaToken,
              fullName,
              tempGroupSponsorId,
              linkedIn,
              title,
              company,
              location,
          }: {
              email: string;
              password: string;
              captchaToken: string;
              fullName: string;
              tempGroupSponsorId: string;
              linkedIn?: string;
              title?: string;
              company?: string;
              location?: string;
          }) => Promise<AuthError | null>)
        | null;
    signInWithOAuth: ((provider: Provider) => Promise<AuthError | null>) | null;
    signInWithPassword:
        | (({
              email,
              password,
              captchaToken,
          }: {
              email: string;
              password: string;
              captchaToken: string;
          }) => Promise<AuthError | null>)
        | null;
    verifyAccount:
        | (({
              verificationCode,
              email,
              type,
          }: {
              verificationCode: string;
              email: string;
              type: 'invite' | 'recovery' | 'email' | null;
          }) => Promise<AuthError | null>)
        | null;
    signOut: (() => Promise<void>) | null;
    hasInitialized: boolean;
    isSignedIn: boolean;
    session: Session | null;
    userId: string;
    userEmail: string;
    userCreatedAt: string;
    getUserData: (() => Promise<void>) | null;
    getProfiles: (() => Promise<void>) | null;
    userProfile: ProfileData | null;
    groupProfiles: Array<ProfileData>;
}

const authDefaultState: AuthContextState = {
    hasInitialized: false,
    signUp: null,
    signInWithOAuth: null,
    signInWithPassword: null,
    verifyAccount: null,
    signOut: null,
    session: null,
    isSignedIn: false,
    userId: '',
    userEmail: '',
    userCreatedAt: '',
    getUserData: null,
    getProfiles: null,
    userProfile: null,
    groupProfiles: [],
};

const AuthContext = createContext(authDefaultState);

export const AuthProvider = ({ children }: PropsWithChildren) => {
    const { showErrorToast } = useToast();

    const [hasInitialized, setHasInitialized] = useState<boolean>(false);
    const [isSignedIn, setIsSignedIn] = useState<boolean>(false);
    const [session, setSession] = useState<Session | null>(null);
    const [userId, setUserId] = useState<string>('');
    const [userEmail, setUserEmail] = useState<string>('');
    const [userCreatedAt, setUserCreatedAt] = useState<string>('');
    const [userProfile, setUserProfile] = useState<ProfileData | null>(null);
    const [groupProfiles, setGroupProfiles] = useState<Array<ProfileData>>([]);

    useEffect(() => {
        const checkIsSignedIn = async () => {
            const { data, error } = await supabase.auth.getSession();
            if (error == null && data.session != null) {
                setIsSignedIn(true);
            }
            setHasInitialized(true);
        };
        checkIsSignedIn();
    }, []);

    useEffect(() => {
        const fetchAndSetSession = async () => {
            if (isSignedIn) {
                const { data, error } = await supabase.auth.getSession();

                if (error == null) {
                    setSession(data.session);
                } else {
                    showErrorToast(error.message);
                }
            } else {
                setSession(null);
            }
        };
        fetchAndSetSession();
    }, [isSignedIn, showErrorToast]);

    const signUp = useCallback(
        async ({
            email,
            password,
            captchaToken,
            fullName,
            tempGroupSponsorId,
            linkedIn,
            title,
            company,
            location,
        }: {
            email: string;
            password: string;
            captchaToken: string;
            fullName: string;
            tempGroupSponsorId: string;
            linkedIn?: string;
            title?: string;
            company?: string;
            location?: string;
        }) => {
            const { error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    captchaToken,
                    data: {
                        full_name: fullName,
                        temp_group_sponsor_id: tempGroupSponsorId,
                        linkedin: linkedIn,
                        title,
                        company,
                        location,
                    },
                },
            });

            return error;
        },
        []
    );

    const signInWithOAuth = useCallback(async (provider: Provider) => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider,
        });

        return error;
    }, []);

    const signInWithPassword = useCallback(
        async ({
            email,
            password,
            captchaToken,
        }: {
            email: string;
            password: string;
            captchaToken: string;
        }) => {
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password,
                options: { captchaToken },
            });

            if (error == null) {
                setIsSignedIn(true);
            }

            return error;
        },
        []
    );

    const verifyAccount = useCallback(
        async ({
            verificationCode,
            email,
            type,
        }: {
            verificationCode: string;
            email: string;
            type: 'invite' | 'recovery' | 'email' | null;
        }) => {
            const { error } = await supabase.auth.verifyOtp({
                token: verificationCode,
                email,
                type: type ?? 'recovery',
            });

            if (error == null) {
                setIsSignedIn(true);
            }

            return error;
        },
        []
    );

    const signOut = useCallback(async () => {
        const { error } = await supabase.auth.signOut();

        if (error == null) {
            setIsSignedIn(false);
            setUserId('');
            setUserEmail('');
            setUserProfile(null);
            setGroupProfiles([]);
        } else {
            showErrorToast(error.message);
        }
    }, [showErrorToast]);

    const getUserData = useCallback(async () => {
        const {
            data: { user },
            error,
        } = await supabase.auth.getUser();

        if (error == null) {
            setUserId(user?.id ?? '');
            setUserEmail(user?.email ?? '');
            setUserCreatedAt(user?.created_at ?? '');
        } else {
            signOut();
            showErrorToast(error.message);
        }
    }, [showErrorToast, signOut]);

    useEffect(() => {
        if (isSignedIn) {
            getUserData();
        }
    }, [getUserData, isSignedIn]);

    const getProfiles = useCallback(async () => {
        const { data: userProfileData, error: userProfileError } =
            await supabase.from('profiles').select('*').eq('id', userId);

        const { data: memberProfilesData, error: memberProfilesError } =
            await supabase
                .from('profiles')
                .select('*')
                .contains(
                    'member_groups',
                    userProfileData?.[0]?.is_coach
                        ? userProfileData?.[0]?.coach_groups ?? []
                        : userProfileData?.[0]?.member_groups ?? []
                )
                .neq('id', userId);

        const filteredMemberProfilesData =
            memberProfilesData?.filter(
                (memberProfile) => memberProfile.is_test !== true
            ) ?? [];

        if (memberProfilesError == null && userProfileError == null) {
            if (userProfileData[0] != null) {
                filteredMemberProfilesData.unshift(userProfileData[0]);
            }
            setUserProfile(userProfileData[0] ?? null);
            setGroupProfiles(filteredMemberProfilesData);
        } else {
            if (userProfileError != null) {
                showErrorToast(userProfileError.message ?? '');
            }
            if (memberProfilesError != null) {
                showErrorToast(memberProfilesError.message ?? '');
            }
        }
    }, [showErrorToast, userId]);

    useEffect(() => {
        if (userId !== '') {
            getProfiles();
        } else {
            setGroupProfiles([]);
        }
    }, [getProfiles, userId]);

    return (
        <AuthContext.Provider
            value={{
                hasInitialized,
                signUp,
                signInWithOAuth,
                signInWithPassword,
                verifyAccount,
                signOut,
                isSignedIn,
                session,
                userId,
                userEmail,
                userCreatedAt,
                getUserData,
                getProfiles,
                userProfile,
                groupProfiles,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
